import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'info' | 'warning' | 'danger';
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = 'info'
}) => {
  const { darkMode } = useTheme();
  
  if (!isOpen) return null;
  
  // Determine color scheme based on type
  const getColors = () => {
    switch (type) {
      case 'danger':
        return {
          bg: darkMode ? 'bg-red-900 bg-opacity-20 border-red-800' : 'bg-red-50 border-red-200',
          icon: darkMode ? 'text-red-300' : 'text-red-600',
          button: darkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-600 hover:bg-red-700',
          iconBg: darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-100'
        };
      case 'warning':
        return {
          bg: darkMode ? 'bg-amber-900 bg-opacity-20 border-amber-800' : 'bg-amber-50 border-amber-200',
          icon: darkMode ? 'text-amber-300' : 'text-amber-600',
          button: darkMode ? 'bg-amber-600 hover:bg-amber-700' : 'bg-amber-600 hover:bg-amber-700',
          iconBg: darkMode ? 'bg-amber-900 bg-opacity-30' : 'bg-amber-100'
        };
      default: // info
        return {
          bg: darkMode ? 'bg-blue-900 bg-opacity-20 border-blue-800' : 'bg-blue-50 border-blue-200',
          icon: darkMode ? 'text-blue-300' : 'text-blue-600',
          button: darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700',
          iconBg: darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-100'
        };
    }
  };
  
  const colors = getColors();
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
      <div 
        className={`${colors.bg} p-6 rounded-lg shadow-lg max-w-md w-full border transform transition-all animate-scaleIn`}
      >
        <div className="flex items-start mb-4">
          <div className={`${colors.iconBg} p-2 rounded-full mr-4`}>
            {type === 'danger' && (
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 ${colors.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            )}
            {type === 'warning' && (
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 ${colors.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            {type === 'info' && (
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 ${colors.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
          </div>
          <div>
            <h2 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>{title}</h2>
            <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{message}</p>
          </div>
        </div>
        
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-md transition-colors ${
              darkMode 
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
            }`}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 rounded-md text-white transition-colors ${colors.button}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};
